/**
 * Shared pieces of the program-admin Reports section on `/programs`.
 *
 * The gate, the period formatting and the download-error mapping live here
 * rather than in the component so they can be unit tested the same way
 * `programVisuals` is.
 */

import type { User } from '@features/auth/services/authService';

/**
 * Only finished reports reach the portal. `generating` and `failed` rows are
 * operational state the OEM has no use for, and a `failed` row is also a retry
 * counter rather than a document.
 */
export const PROGRAM_REPORT_VISIBLE_STATUS = 'generated';

export type ProgramReportRow = {
  id: number;
  program_id: number;
  report_type: string;
  period_start: string;
  period_end: string;
  status: string;
  row_count?: number | null;
  claim_count?: number | null;
  total_program_amount?: number | string | null;
  generated_at?: string | null;
  generated_by?: string | null;
  source?: string | null;
  created_at?: string | null;
  program?: { id: number; name?: string | null } | null;
};

/**
 * Roles allowed to see program reports here. `admin` is included to match what
 * `app-get-program-report-url` already authorizes (admin, or a program-admin with
 * a `usertoprograms` row) — without it an admin could presign a report through the
 * endpoint while the UI told them the section did not exist.
 */
const PROGRAM_REPORT_ROLES = ['program-admin', 'admin'];

/**
 * Role-gated, not `program_visuals`-gated: visuals is the per-customer switch
 * for the dealer-facing coverage view, and program-wide financial detail is not
 * dealer-facing. Every `dealer` and `dealer-admin` login stays dark to this —
 * that, not hiding it from staff, is the requirement.
 */
export const canViewProgramReports = (user?: Pick<User, 'profile'> | null): boolean =>
  PROGRAM_REPORT_ROLES.includes(user?.profile?.default_role ?? '');

/**
 * `period_start` / `period_end` are Postgres `date` columns, so they arrive as
 * bare `YYYY-MM-DD`. Handing that to `new Date()` lands on UTC midnight, which
 * renders as the previous day anywhere west of Greenwich — a semi-monthly
 * period would read as ending on the 14th instead of the 15th.
 */
const parseDateOnly = (value?: string | null): Date | null => {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return isNaN(date.getTime()) ? null : date;
};

const MONTH_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
const MONTH_DAY_YEAR: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

/** `Jul 1 – Jul 15, 2026`, dropping the repeated year unless the period straddles one. */
export const formatReportPeriod = (start?: string | null, end?: string | null): string => {
  const from = parseDateOnly(start);
  const to = parseDateOnly(end);
  if (!from || !to) return '—';
  const sameYear = from.getFullYear() === to.getFullYear();
  return `${from.toLocaleDateString(undefined, sameYear ? MONTH_DAY : MONTH_DAY_YEAR)} – ${to.toLocaleDateString(
    undefined,
    MONTH_DAY_YEAR,
  )}`;
};

/** `generated_at` is a timestamptz, so the plain parse is correct here. */
export const formatGeneratedAt = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

/** A program admin can span programs; the column is noise when there is only one. */
export const shouldShowProgramColumn = (reports: ProgramReportRow[]): boolean =>
  new Set(reports.map(report => report.program?.id ?? report.program_id)).size > 1;

/**
 * The read endpoint re-checks role and program membership, so a 403 here means
 * the caller genuinely lost access rather than that something broke.
 */
export const programReportErrorMessage = (error: unknown): string => {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (status === 403 || status === 401) return `You don't have access to that report.`;
  if (status === 404) return `That report is no longer available.`;
  return `Couldn't get the report download link. Please try again.`;
};
